import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getConnectionStatus,
  getOAuthUrl,
  disconnectGoogle,
  triggerSync,
  getSyncStatus as fetchSyncStatus,
  getSuggestions,
  updateSuggestion,
  batchUpdateSuggestions,
  executeSuggestion,
  batchExecuteSuggestions,
} from '../../api/meetingInsights';
import SuggestionList from './SuggestionList';
import './MeetingInsightsPage.css';

export default function MeetingInsightsPage() {
  const { getAuthHeader } = useAuth();
  const auth = getAuthHeader();

  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [syncStatus, setSyncStatus] = useState({ status: 'idle' });
  const syncPollRef = useRef(null);

  const [tab, setTab] = useState('pending');
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // ── Load connection status ──────────────────────────────────────────────

  const loadConnection = useCallback(async () => {
    try {
      const data = await getConnectionStatus(auth);
      setConnection(data.ok ? data.connection : null);
    } catch (err) {
      setError('Failed to load connection status');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  // Listen for OAuth popup completion
  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'meeting-insights-connected') {
        loadConnection();
        loadSuggestions();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [loadConnection]);

  // ── OAuth connect ───────────────────────────────────────────────────────

  const handleConnect = async () => {
    try {
      const data = await getOAuthUrl(auth);
      if (data.ok && data.url) {
        window.open(data.url, 'google-calendar-connect', 'width=600,height=700');
      } else {
        setError(`Failed to start Google connection: ${data.error || 'unknown error'}`);
      }
    } catch (err) {
      setError(`Failed to start Google connection: ${err.message || err}`);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? This will stop meeting insights.')) return;
    try {
      await disconnectGoogle(auth);
      setConnection(null);
      setSuggestions([]);
    } catch (err) {
      setError('Failed to disconnect');
    }
  };

  // ── Sync ────────────────────────────────────────────────────────────────

  const handleSync = async () => {
    try {
      setError(null);
      const data = await triggerSync(auth, true);
      if (data.ok) {
        setSyncStatus({ status: 'running', syncRunId: data.syncRunId });
        startSyncPoll();
      }
    } catch (err) {
      setError('Failed to start sync');
    }
  };

  const startSyncPoll = () => {
    if (syncPollRef.current) clearInterval(syncPollRef.current);
    syncPollRef.current = setInterval(async () => {
      try {
        const data = await fetchSyncStatus(auth);
        setSyncStatus(data);
        if (data.status !== 'running') {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
          loadSuggestions();
        }
      } catch {
        clearInterval(syncPollRef.current);
        syncPollRef.current = null;
      }
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (syncPollRef.current) clearInterval(syncPollRef.current);
    };
  }, []);

  // ── Suggestions ─────────────────────────────────────────────────────────

  const loadSuggestions = useCallback(async () => {
    setLoadingSuggestions(true);
    try {
      const data = await getSuggestions(tab, auth);
      if (data.ok) setSuggestions(data.suggestions || []);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [tab, auth]);

  useEffect(() => {
    if (connection) loadSuggestions();
  }, [tab, connection, loadSuggestions]);

  const handleUpdate = async (id, action, modifiedText) => {
    await updateSuggestion(id, action, modifiedText, auth);
    loadSuggestions();
  };

  const handleExecute = async (id) => {
    const result = await executeSuggestion(id, auth);
    if (result.ok) loadSuggestions();
    else setError(result.error || 'Execution failed');
  };

  const handleBatchUpdate = async (ids, action) => {
    await batchUpdateSuggestions(ids, action, auth);
    loadSuggestions();
  };

  const handleBatchExecute = async (ids) => {
    await batchExecuteSuggestions(ids, auth);
    loadSuggestions();
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="mi-page"><div className="mi-loading">Loading...</div></div>;
  }

  return (
    <div className="mi-page">
      <div className="mi-header">
        <h1>Meeting Insights</h1>
        <a href="/" className="mi-back-link">Back to Assistant</a>
      </div>

      {error && (
        <div className="mi-error">
          {error}
          <button
            className="mi-btn mi-btn-small mi-btn-secondary"
            style={{ marginLeft: 8 }}
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Connection Panel */}
      <div className="mi-connection">
        <div className="mi-connection-info">
          {connection ? (
            <>
              <p className="mi-connection-status">
                Connected as <span className="mi-connection-email">{connection.email}</span>
              </p>
              {connection.lastSyncAt && (
                <p className="mi-connection-sync">
                  Last synced: {new Date(connection.lastSyncAt).toLocaleString()}
                </p>
              )}
            </>
          ) : (
            <p className="mi-connection-status">
              Connect your Google account to analyze meeting transcripts
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {connection ? (
            <>
              <button className="mi-btn mi-btn-primary" onClick={handleSync} disabled={syncStatus.status === 'running'}>
                {syncStatus.status === 'running' ? 'Syncing...' : 'Sync Now'}
              </button>
              <button className="mi-btn mi-btn-danger mi-btn-small" onClick={handleDisconnect}>
                Disconnect
              </button>
            </>
          ) : (
            <button className="mi-btn mi-btn-primary" onClick={handleConnect}>
              Connect Google Calendar
            </button>
          )}
        </div>
      </div>

      {/* Sync Status */}
      {syncStatus.status === 'running' && (
        <div className="mi-sync-bar">
          <div className="mi-sync-spinner" />
          <span>
            Syncing calendar events...
            {syncStatus.eventsProcessed > 0 && ` (${syncStatus.eventsProcessed} processed, ${syncStatus.suggestionsCreated} suggestions)`}
          </span>
        </div>
      )}
      {syncStatus.status === 'completed' && syncStatus.suggestionsCreated > 0 && (
        <div className="mi-sync-bar" style={{ background: '#e8f5e9' }}>
          Sync complete: {syncStatus.eventsProcessed} events processed, {syncStatus.suggestionsCreated} new suggestions
        </div>
      )}

      {/* Suggestions */}
      {connection && (
        <>
          <div className="mi-tabs">
            {['pending', 'accepted', 'rejected'].map(t => (
              <button
                key={t}
                className={`mi-tab ${tab === t ? 'active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
                {t === 'pending' && suggestions.length > 0 && tab === 'pending' && (
                  <span className="mi-tab-count">{suggestions.length}</span>
                )}
              </button>
            ))}
          </div>

          {loadingSuggestions ? (
            <div className="mi-loading">Loading suggestions...</div>
          ) : (
            <SuggestionList
              suggestions={suggestions}
              onUpdate={handleUpdate}
              onExecute={handleExecute}
              onBatchUpdate={handleBatchUpdate}
              onBatchExecute={handleBatchExecute}
              showActions={tab !== 'rejected'}
            />
          )}
        </>
      )}

    </div>
  );
}
